import IconButton from "../../buttons/IconButton";
import { PlusIcon, DeleteIcon } from "../../Icons";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { BasicNameValue } from "../../../types/types";

type ParameterPresetChildProps = {
  index: number;
  currentParameter: BasicNameValue;
  paramType: string;
  localAvailableParams: string[];
  canAdd?: boolean; // Optional prop to control the add button
  canEdit?: boolean; // Optional prop to control editability
  canDelete?: boolean; // Optional prop to control delete the child element
  onAdd?: (newChild: BasicNameValue) => void;
  onAddLocalParameter?: (newParameterName: string, index: number, value?: string) => void;
  onDelete?: (index: number) => void;
};

const ParameterPresetChild: React.FC<ParameterPresetChildProps> = ({ index, currentParameter, paramType, onAdd, onDelete, canAdd, onAddLocalParameter, canEdit, canDelete, localAvailableParams }) => {
  const [activeParameterPreset, setActiveParameterPreset] = useState<BasicNameValue>(currentParameter);
  const [newParameterName, setNewParameterName] = useState<string>(""); // Placeholder for modifying parameter name
  const [newParameterValue, setNewParameterValue] = useState<string>(""); // Placeholder for modifying parameter value

  // Always sync local state with prop
  useEffect(() => {
    setActiveParameterPreset(currentParameter);
  }, [currentParameter]);

  // No local parameters state; always use localAvailableParams from parent

  return (
    <div className="w-full grid grid-cols-9 gap-2">
      <div className="col-span-4">
        <Dropdown
          value={activeParameterPreset?.name || ""}
          options={localAvailableParams || []}
          pt={{
            root: {
              className: 'min-w-full'
            },
            input: {
              className: 'py-2'
            }
          }}
          onChange={(e) => {
            setActiveParameterPreset(prev => {
              if (!prev) return prev;
              const updatedPreset = Object.assign(
                Object.create(Object.getPrototypeOf(prev)),
                { ...prev, name: e.value, value: prev.value || "" }
              );
              return updatedPreset;
            });
          }}
          filter
          filterPlaceholder="Search Parameter"
          placeholder={activeParameterPreset?.name || "Select Parameter"}
          emptyMessage="No parameters available"
          showClear
          panelFooterTemplate={() => (
            <div
              className="flex items-center gap-2 p-2 border-t bg-white border-gray-200 dark:border-gray-700"
              style={{ position: "static" }}
            >
              <InputText
                value={newParameterName}
                onChange={(e) => {
                  const name = e.target.value;
                  setNewParameterName(name);
                }}
                placeholder="New Parameter Name"
                className="flex-1 bg-white"
              />
              <IconButton
                title="Add New Parameter"
                additionalClassName=""
                severity="secondary"
                icon={<PlusIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                onClick={() => {
                  if (!newParameterName.trim()) return;
                  // Always add to parent availableParams (so dropdowns update globally)
                  if (onAddLocalParameter) {
                    onAddLocalParameter(newParameterName, index, undefined);
                  }
                  // Select the new parameter in the dropdown
                  setActiveParameterPreset(prev => {
                    if (!prev) return prev;
                    const updatedPreset = Object.assign(
                      Object.create(Object.getPrototypeOf(prev)),
                      { ...prev, name: newParameterName, value: prev.value || "" }
                    );
                    return updatedPreset;
                  });
                  setNewParameterName("");
                }}
              />
            </div>
          )}
          panelClassName="min-w-full"
          appendTo={document.body}
          disabled={!canEdit}
        />
      </div>
      <InputText
        value={activeParameterPreset?.value.trim() || newParameterValue}
        onChange={(e) => {
          setNewParameterValue(e.target.value);
          onAddLocalParameter(newParameterName, index, e.target.value);
        }}

        placeholder={newParameterValue || activeParameterPreset?.value || "Enter Preset Value"}
        className="min-w-full col-span-4"
        disabled={!canEdit}
        required
      />
      {canEdit && (
        <div className="flex items-center space-x-2 !ml-2">
          {
            canDelete && (
              <IconButton
                title={`Delete Parameter ${index + 1}`}
                severity="danger"
                icon={<DeleteIcon className="w-4 h-4 fill-white inline cursor-pointer" />}
                onClick={() => onDelete(index)}
              />
            )
          }
          {canAdd && (
            <IconButton
              title="Add New Parameter (Local Only)"
              additionalClassName="bg-gm-black"
              severity="secondary"
              icon={<PlusIcon className="w-4 h-4 fill-white inline cursor-pointer" />}
              onClick={() => {
                const newParameter = new BasicNameValue({ name: '', value: '' });
                onAdd(newParameter);
              }}
              disabled={
                (() => {
                  // @ts-ignore
                  const key = (ParameterPresetChild as any).key || (ParameterPresetChild as any).props?.key;
                  if (typeof key === "string") {
                    const parts = key.split("-");
                    return parts[parts.length - 1] === "0";
                  }
                  return false;
                })()
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ParameterPresetChild;
